import { useTranslation } from "react-i18next";
import { Button } from "antd";

import { formatPriceInfo } from "../../utils/price";

import "./SubscriptionPlan.css";

import type { SubscriptionPlan as PlanData } from "../../api/subscription";
import type { PlanDescription } from "../../config";

interface Props {
  plan: PlanData;
  description: PlanDescription;
  isYearly: boolean;
  isActive: boolean;
  isAutopaymentActive: boolean;
  currentPeriod: string | null;
  canceledAt: string | null;
  expireAt: string | null;
  onSelect: (plan: PlanData) => void;
}

export const SubscriptionPlan = ({
  plan,
  description,
  isYearly,
  isActive,
  isAutopaymentActive,
  currentPeriod,
  canceledAt,
  expireAt,
  onSelect,
}: Props) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as "en" | "ru";

  const finalPrice = isYearly
    ? (plan.yearlyPriceDiscounted ?? plan.yearlyPrice)
    : (plan.monthlyPriceDiscounted ?? plan.monthlyPrice);
  const oldPrice =
    isYearly && plan.yearlyPriceDiscounted
      ? plan.yearlyPrice
      : !isYearly && plan.monthlyPriceDiscounted
        ? plan.monthlyPrice
        : null;

  const isPeriodDifferent =
    (currentPeriod === "monthly" && isYearly) ||
    (currentPeriod === "yearly" && !isYearly);
  const selectLabel =
    plan.id === "pomelo"
      ? t("app.configure", { defaultValue: "Configure" })
      : t("app.select", { defaultValue: "Select" });

  const renderButton = () => {
    if (plan.id === "pomelo") {
      return (
        <Button type="primary" size="large" onClick={() => onSelect(plan)}>
          {selectLabel}
        </Button>
      );
    }
    if (isAutopaymentActive) {
      if (isActive && !isPeriodDifferent) {
        return (
          <Button
            variant="outlined"
            className="subscription-plan__btn--current"
            size="large"
            onClick={() => onSelect(plan)}
          >
            {t("subscription.yourPlan")}
          </Button>
        );
      }
      if (isActive && isPeriodDifferent) {
        return (
          <Button size="large" onClick={() => onSelect(plan)}>
            {t("subscription.changeInterval")}
          </Button>
        );
      }
      return (
        <Button type="primary" size="large" onClick={() => onSelect(plan)}>
          {selectLabel}
        </Button>
      );
    }
    // No autopayment
    if (plan.id === "lemon" && isActive) {
      return (
        <Button
          variant="outlined"
          className="subscription-plan__btn--current"
          size="large"
        >
          {t("subscription.yourPlan")}
        </Button>
      );
    }
    if (plan.id === "lemon" && canceledAt) {
      return (
        <Button
          variant="outlined"
          className="subscription-plan__btn--current"
          size="large"
        >
          {t("subscription.yourPlan")} since {expireAt}
        </Button>
      );
    }
    if (plan.id !== "lemon" && isActive && !canceledAt) {
      return (
        <Button size="large" onClick={() => onSelect(plan)}>
          {t("subscription.renew")}
        </Button>
      );
    }
    return (
      <Button size="large" type="primary" onClick={() => onSelect(plan)}>
        {selectLabel}
      </Button>
    );
  };

  const visibleFeatures = (features: PlanDescription["topList"]) =>
    features.filter((f) => f.locales.includes(locale));

  return (
    <article className={`subscription-plan subscription-plan--${plan.id}`}>
      <header className="subscription-plan__header">
        <div className="subscription-plan__title">{plan.name}</div>
        <div className="subscription-plan__price">
          <span className="subscription-plan__price-value">
            {finalPrice ? formatPriceInfo(finalPrice) : null}
            {isYearly ? (
              <span className="subscription-plan__price-period">
                /{t("subscription.yearly")}
              </span>
            ) : (
              <span className="subscription-plan__price-period">
                /{t("subscription.monthly")}
              </span>
            )}
          </span>
          {oldPrice && (
            <span className="subscription-plan__price-old">
              {formatPriceInfo(oldPrice)}
            </span>
          )}
          {isYearly && plan.yearlyPrice?.amount > 0 && !oldPrice && (
            <span className="subscription-plan__price-monthly">
              {formatPriceInfo({
                amount: plan.yearlyPrice.amount / 12,
                currency: plan.yearlyPrice.currency,
              })}
              /{t("subscription.monthly")}
            </span>
          )}
        </div>
      </header>

      <ul className="subscription-plan__features">
        {visibleFeatures(description.topList).map((feature, i) => (
          <li key={i}>
            {t(feature.key, {
              count: feature.count,
              ...(feature.options as Record<string, string>),
            })}
          </li>
        ))}
      </ul>

      <div className="subscription-plan__bottom">
        <div className="subscription-plan__bottom-title">
          {t(description.bottomListTitleKey)}
        </div>
        <ul className="subscription-plan__features">
          {visibleFeatures(description.bottomList).map((feature, i) => (
            <li key={i}>
              {t(feature.key, {
                count: feature.count,
                ...(feature.options as Record<string, string>),
              })}
            </li>
          ))}
        </ul>
      </div>

      <div className="subscription-plan__btn">{renderButton()}</div>
    </article>
  );
};
